import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TYPES = [
  { value: "church", label: "Church / Faith community" },
  { value: "nonprofit", label: "Nonprofit organization" },
  { value: "government", label: "Government / Area Agency on Aging" },
  { value: "support_group", label: "Support group" },
  { value: "meals_program", label: "Meals / food program" },
  { value: "transportation", label: "Volunteer transportation" },
  { value: "other", label: "Other" },
];

const EMPTY = {
  organization_name: "",
  contact_name: "",
  email: "",
  phone: "",
  org_type: "",
  service_area: "",
  message: "",
};

export function CommunityPartnerInquiryModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const reset = () => {
    setForm(EMPTY);
    setSubmitted(false);
  };

  const handleClose = () => {
    onClose();
    // Delay reset so the "Thanks!" screen doesn't flash away as the modal closes
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    const required = ["organization_name", "contact_name", "email", "phone", "org_type", "message"];
    const missing = required.filter((k) => !form[k]?.trim());
    if (missing.length) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/community-partner/inquiries`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent
        data-testid="community-partner-inquiry-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="inline-flex w-16 h-16 rounded-full bg-secondary/15 items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-secondary" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-text-primary">Thank you!</h3>
            <p className="text-text-secondary font-body max-w-md mx-auto">
              We've received your inquiry. A Tyvena team member will reach out within 2 business days to talk about becoming a Community Partner.
            </p>
            <Button
              data-testid="community-partner-modal-close"
              onClick={handleClose}
              className="mt-4 bg-primary text-white hover:bg-[#238C82] rounded-full font-semibold"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/15 flex items-center justify-center">
                  <HeartHandshake className="w-5 h-5 text-secondary" />
                </div>
                <DialogTitle className="text-xl font-heading">
                  Community Partner Inquiry
                </DialogTitle>
              </div>
              <DialogDescription className="pt-2 font-body text-text-secondary">
                Tell us about your organization. Community Partners are listed at no cost — in exchange for helping share Tyvena with the families you serve.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div>
                <Label className="font-body font-semibold text-text-primary">
                  Organization name *
                </Label>
                <Input
                  data-testid="cp-org-name"
                  value={form.organization_name}
                  onChange={handleChange("organization_name")}
                  placeholder="e.g., Charlotte Alzheimer's Association"
                  className="mt-2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Your name *
                  </Label>
                  <Input
                    data-testid="cp-contact-name"
                    value={form.contact_name}
                    onChange={handleChange("contact_name")}
                    placeholder="Jane Doe"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Organization type *
                  </Label>
                  <select
                    data-testid="cp-org-type"
                    value={form.org_type}
                    onChange={handleChange("org_type")}
                    className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-body"
                  >
                    <option value="">Choose one…</option>
                    {ORG_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Email *
                  </Label>
                  <Input
                    data-testid="cp-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@yourorg.org"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Phone *
                  </Label>
                  <Input
                    data-testid="cp-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="(555) 555-5555"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary">
                  Service area <span className="font-normal text-text-secondary">(optional)</span>
                </Label>
                <Input
                  data-testid="cp-service-area"
                  value={form.service_area}
                  onChange={handleChange("service_area")}
                  placeholder="Zip codes or city/state you serve — e.g., Charlotte NC, 28202"
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary">
                  What does your organization offer, and how would you like to partner with Tyvena? *
                </Label>
                <Textarea
                  data-testid="cp-message"
                  value={form.message}
                  onChange={handleChange("message")}
                  rows={4}
                  placeholder="Tell us about your services, who you serve, and how we might work together (e.g., sharing our brochures, hosting events, cross-promotion)."
                  className="mt-2 font-body"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  data-testid="cp-cancel"
                  onClick={handleClose}
                  variant="ghost"
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="cp-submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-secondary text-white hover:bg-secondary/90 rounded-full font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Submit inquiry"
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
